import { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { ChunkErrorBoundary } from './ChunkErrorBoundary';

/**
 * Rendered when the map chunk cannot be loaded at all (already reloaded once, or
 * offline). Hands control straight back to the carousel: without this the app would
 * sit on an empty gradient forever, since the map is what normally signals that its
 * fly-to animation is done.
 *
 * @param onGiveUp Callback switching the background back to the carousel
 */
function MapUnavailable({ onGiveUp }: { onGiveUp: () => void }) {
    useEffect(() => {
        onGiveUp();
    }, [onGiveUp]);

    return <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />;
}
import { AnimatePresence, motion } from 'framer-motion';
import { BackgroundCarousel } from './BackgroundCarousel';
import { AirportText } from '@/components/ui/AirportText';
import { useBandwidthExperience } from '@/hooks/useBandwidthExperience';
import { CountryMapWithPois } from './CountryMapWithPois';

const LeafletMapBackground = lazy(() => import('./LeafletMapBackground').then(module => ({ default: module.LeafletMapBackground })));

// OpenStreetMap Nominatim API for geocoding
// Free usage limits apply (see https://operations.osmfoundation.org/policies/nominatim/)

interface BackgroundManagerProps {
    destination: string | null;
    images: string[];
}

export function BackgroundManager({ destination, images }: BackgroundManagerProps) {
    const { experienceMode, isReady } = useBandwidthExperience();
    const [mode, setMode] = useState<'map' | 'carousel'>('carousel');
    const [targetCoords, setTargetCoords] = useState<[number, number] | null>(null);
    const prevDestRef = useRef<string | null>(null);

    useEffect(() => {
        if (import.meta.env.DEV) console.log('[BackgroundManager] Received destination:', destination);

        // If destination changes and it's a new valid string
        if (destination && destination !== prevDestRef.current) {
            const normalizedDest = destination.trim();
            if (import.meta.env.DEV) console.log(`[BackgroundManager] Fetching coords for: "${normalizedDest}"`);

            // Fetch coordinates from Nominatim
            fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(normalizedDest)}&limit=1`, {
                headers: {
                    'User-Agent': 'AnoukVoiceAgent/1.0' // Required by OSM policy
                }
            })
                .then(res => res.json())
                .then(data => {
                    if (data && data.length > 0) {
                        const lat = parseFloat(data[0].lat);
                        const lon = parseFloat(data[0].lon);
                        if (import.meta.env.DEV) console.log(`[Background] Geocoding success for ${destination}:`, [lat, lon]);

                        setTargetCoords([lat, lon]);
                        setMode('map');
                    } else {
                        console.warn(`[Background] No coordinates found for ${destination}`);
                        setMode('carousel');
                    }
                })
                .catch(err => {
                    console.error('[Background] Geocoding error:', err);
                    setMode('carousel');
                });

            prevDestRef.current = destination;
        }
    }, [destination]);

    const handleMapAnimationComplete = () => {
        if (import.meta.env.DEV) console.log('[Background] Map flight complete. Switching to CAROUSEL.');
        setMode('carousel');
    };

    return (
        <div className="absolute inset-0 min-h-[100dvh] z-0">
            {/* Map Layer - Always present but hidden/shown via opacity for smooth transitions? 
            Or unmounted? Leaflet prefers staying mounted for performance usually, 
            but unmounting ensures clean state. Let's try AnimatePresence fade.
        */}
            <AnimatePresence mode="wait">
                {mode === 'map' && targetCoords && (
                    <motion.div
                        key="map"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, transition: { duration: 1.5 } }}
                        className="absolute inset-0 min-h-[100dvh] z-10"
                    >
                        {!isReady ? (
                            <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
                                <div className="text-white/50 text-sm">Chargement...</div>
                            </div>
                        ) : experienceMode === 'light' ? (
                            <CountryMapWithPois
                                destinationName={destination ?? ''}
                                targetCoords={targetCoords}
                                onAnimationComplete={handleMapAnimationComplete}
                            />
                        ) : (
                            <>
                                <ChunkErrorBoundary fallback={
                                    <MapUnavailable onGiveUp={handleMapAnimationComplete} />
                                }>
                                    <Suspense fallback={
                                        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
                                            <div className="text-white/50 text-sm">Chargement de la carte...</div>
                                        </div>
                                    }>
                                        <LeafletMapBackground
                                            center={targetCoords}
                                            zoom={6}
                                            onAnimationComplete={handleMapAnimationComplete}
                                            tileVariant="light"
                                        />
                                    </Suspense>
                                </ChunkErrorBoundary>
                                <div className="absolute top-1/4 left-0 right-0 flex justify-center z-20 pointer-events-none">
                                    <div className="bg-black/40 backdrop-blur-md border-y border-white/20 py-4 w-full text-center">
                                        <p className="text-white/80 text-sm mb-1 uppercase tracking-[0.2em]">Destination</p>
                                        <h2 className="text-4xl md:text-6xl text-white font-bold drop-shadow-2xl">
                                            <AirportText text={`EN ROUTE POUR ${destination}`} />
                                        </h2>
                                    </div>
                                </div>
                            </>
                        )}
                    </motion.div>
                )}

                {mode === 'carousel' && (
                    <motion.div
                        key="carousel"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1, transition: { duration: 1.5, delay: 0.5 } }} // Fade in after map fades
                        exit={{ opacity: 0 }}
                        className="absolute inset-0 min-h-[100dvh] z-0"
                    >
                        <BackgroundCarousel images={images} />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
